import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { AuctionTable } from '../components/problems/AuctionTable';
import { HandDisplay } from '../components/problems/HandDisplay';
import { TableFrame } from '../components/table-session/TableFrame';
import { SeatBoardLayout } from '../components/table-session/SeatBoardLayout';
import { VulnerabilityBoard } from '../components/table-session/VulnerabilityBoard';
import { SeatLabel } from '../components/table-session/SeatLabel';
import { StatusLine } from '../components/table-session/StatusLine';
import { BidExplanationPanel } from '../components/predicted-jeff-bids/BidExplanationPanel';
import { CategoryName } from '../components/predicted-jeff-bids/CategoryName';
import { PredictedAuctionTable } from '../components/predicted-jeff-bids/PredictedAuctionTable';
import { PredictionFilterBar } from '../components/predicted-jeff-bids/PredictionFilterBar';
import { predictedJeffBidsApi } from '../api/predictedJeffBidsApi';
import type {
  PredictedHandDetail,
  PredictedHandIndexItem,
  PredictionCategory,
  PredictionOutcomeFilter,
} from '../types/predictedJeffBids';
import type { Position } from '../types/problems';

/**
 * "Predicted Jeff's Bids by AI" (feature 025-ai-predicted-bids; categories and sub-categories
 * since 032-ai-prediction-categories) — a Data Editing surface showing, for every deal the AI
 * engine analysed, the auction Jeff actually bid beside the one the AI predicted, with the AI's
 * reasoning behind every predicted call. Read-mostly: the editor can add a shared correction on
 * predictions that were wrong.
 *
 * The deals are presented under six categories, each with sub-categories, exactly as the
 * prediction program defines them: the reviewer picks a Category (always one — there is no
 * all-categories view), then a Sub-category or all of them, then an outcome. A PREDICTION is the
 * unit here, not a deal: a deal may carry one prediction per sub-category, so an entry is
 * identified by its sub-category and its deal together.
 *
 * It is drawn by the SAME `TableFrame` the practice pages use, with the right-hand panel —
 * normally the learner's call pad — given over to the AI's prediction. That is the whole point of
 * the page: the reviewer reads the real auction and the predicted one side by side, in a layout
 * they already know. There is no bid entry and no score, because nobody is answering anything
 * here (FR-011).
 *
 * Two requests back it: an index of every prediction plus the category tree, loaded once, which
 * makes filtering, jumping and Previous/Next instant; and a per-prediction detail fetch, because
 * the reasoning text would be a multi-megabyte payload if it all arrived up front.
 */

const DIRECTION_NAMES: Record<Position, string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

/** How long to wait after the reviewer settles before writing the bookmark. */
const BOOKMARK_DEBOUNCE_MS = 600;

/** The query keys that deep-link a prediction: the deal, and the sub-category it is shown under. */
const HAND_ID_PARAM = 'id';
const SUBCATEGORY_PARAM = 'sub';

/** An entry in the index: one prediction, identified by its sub-category and its deal. */
interface EntryRef {
  subcategoryId: number;
  tableResultId: number;
}

/** Parse a query value into a positive integer, or null when absent/malformed. */
function parsePositiveInt(raw: string | null): number | null {
  if (!raw) return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function PredictedJeffBidsPage() {
  usePageTitle("Predicted Jeff's Bids by AI — Hawkes Bridge");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The prediction the page was opened with (`?id=` and, optionally, `?sub=`), captured once. A
  // deep link wins over the bookmark.
  const initialRequested = useRef({
    tableResultId: parsePositiveInt(searchParams.get(HAND_ID_PARAM)),
    subcategoryId: parsePositiveInt(searchParams.get(SUBCATEGORY_PARAM)),
  });

  // The latest search params, read imperatively so the URL-sync effect need not depend on them —
  // otherwise it would fire on its own writes, and would fight an external edit of the query.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const [index, setIndex] = useState<PredictedHandIndexItem[]>([]);
  const [categories, setCategories] = useState<PredictionCategory[]>([]);
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexError, setIndexError] = useState<string | null>(null);

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);
  const [outcomeFilter, setOutcomeFilter] = useState<PredictionOutcomeFilter>('all');

  const [current, setCurrent] = useState<EntryRef | null>(null);
  const [detail, setDetail] = useState<PredictedHandDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [selectedOrdinal, setSelectedOrdinal] = useState<number | null>(null);

  // The correction editor (on mismatched hands) reports when its draft is unsaved. While it is,
  // navigating off the hand is held back and the field is flagged rather than silently discarding
  // the edit.
  const [correctionDirty, setCorrectionDirty] = useState(false);
  const [remindUnsavedCorrection, setRemindUnsavedCorrection] = useState(false);

  // The bookmark must not be written back while it is still being restored, or the very first
  // render would overwrite the position the reviewer left off at with the default.
  const bookmarkRestored = useRef(false);

  // Last-viewed hand per filter combination (category x sub-category | all x outcome), so
  // switching any dropdown resumes that view where the reviewer left it rather than dumping them
  // at its first hand. Key is `scopeKey(outcome, category, subcategory)`. Seeded from the server on
  // load and kept in step as the reviewer navigates (see the debounced-write effect), so an
  // in-session switch away and back resumes without waiting on the network. A ref, not state: it
  // is read only inside handlers/effects and never rendered, so it can never drive a re-render or
  // a loop.
  const bookmarksRef = useRef(new Map<string, number | null>());

  // ---------------------------------------------------------------------------------------
  // Load the index and the reviewer's bookmark together, so the first paint is already at the
  // hand they left off at rather than jumping there a moment later.
  // ---------------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      predictedJeffBidsApi.getIndex(),
      // Bookmarks that cannot be read are not worth failing the page for — start at the top.
      predictedJeffBidsApi.getReviewStates().catch(() => null),
    ])
      .then(([indexResponse, reviewStates]) => {
        if (cancelled) return;

        const tree = indexResponse.categories;
        setIndex(indexResponse.hands);
        setCategories(tree);

        // Seed the per-combination map from every stored bookmark BEFORE anything else, so even a
        // deep-link landing still knows where the other views were left off.
        const states = reviewStates?.states ?? [];
        for (const s of states) {
          bookmarksRef.current.set(
            scopeKey(s.outcomeFilter, s.categoryId, s.subcategoryId ?? null),
            s.lastTableResultId
          );
        }

        // The page reopens at the most-recently-touched combination — the first row (newest-first).
        // A stored scope the tree no longer shows (a category or sub-category gone from the
        // definition) falls back to the first category, all sub-categories.
        const state = states[0] ?? null;
        const restoredCategory =
          state && tree.some(c => c.id === state.categoryId)
            ? state.categoryId
            : tree[0]?.id ?? null;
        const restoredSubcategory =
          state && state.subcategoryId !== null && restoredCategory === state.categoryId
            && subcategoriesOf(tree, restoredCategory).has(state.subcategoryId)
            ? state.subcategoryId
            : null;
        const restoredOutcome = state?.outcomeFilter ?? 'all';
        const restoredSet = filterHands(
          indexResponse.hands,
          scopeIds(tree, restoredCategory, restoredSubcategory),
          restoredOutcome
        );

        // A deep link that names a loaded prediction takes precedence over the bookmark: the pair
        // when `sub` is given, else the deal's first entry (the index is in display order).
        const requested = initialRequested.current;
        const linked =
          requested.tableResultId === null
            ? null
            : indexResponse.hands.find(
                h =>
                  h.tableResultId === requested.tableResultId &&
                  (requested.subcategoryId === null || h.subcategoryId === requested.subcategoryId)
              ) ?? null;

        if (linked) {
          // When the linked prediction falls outside the remembered filters, move to its own
          // category with no narrowing, so the link always resolves to that prediction rather
          // than landing on the wrong one or an empty set.
          const inRestoredSet = restoredSet.some(h => sameEntry(h, linked));
          if (inRestoredSet) {
            setCategoryId(restoredCategory);
            setSubcategoryId(restoredSubcategory);
            setOutcomeFilter(restoredOutcome);
          } else {
            setCategoryId(categoryOf(tree, linked.subcategoryId));
            setSubcategoryId(null);
            setOutcomeFilter('all');
          }
          setCurrent({ subcategoryId: linked.subcategoryId, tableResultId: linked.tableResultId });
          return;
        }

        setCategoryId(restoredCategory);
        setSubcategoryId(restoredSubcategory);
        setOutcomeFilter(restoredOutcome);

        // A bookmarked hand outside the restored set (the data changed under it) falls back to
        // the first hand of that set rather than showing nothing. When the scope ITSELF had to
        // be replaced (its category or sub-category is gone), the remembered hand belongs to a
        // view that no longer exists, so the fallback scope starts at its first hand too.
        const scopeRestored =
          state !== null &&
          restoredCategory === state.categoryId &&
          (state.subcategoryId ?? null) === restoredSubcategory;
        const bookmarked = scopeRestored ? state.lastTableResultId : null;
        setCurrent(landingFor(restoredSet, bookmarked, null));
      })
      .catch(() => {
        if (!cancelled) setIndexError('Unable to load the AI predictions. Please try again.');
      })
      .finally(() => {
        if (cancelled) return;
        setIndexLoading(false);
        bookmarkRestored.current = true;
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const scope = useMemo(
    () => scopeIds(categories, categoryId, subcategoryId),
    [categories, categoryId, subcategoryId]
  );

  const filtered = useMemo(
    () => filterHands(index, scope, outcomeFilter),
    [index, scope, outcomeFilter]
  );

  const currentIndex = current === null ? -1 : filtered.findIndex(h => sameEntry(h, current));

  // ---------------------------------------------------------------------------------------
  // Keep the shown prediction inside the filtered set. Changing a filter can strip out the one on
  // screen; landing on the first of the new set is more useful than an empty frame.
  // ---------------------------------------------------------------------------------------
  useEffect(() => {
    if (indexLoading) return;
    if (filtered.length === 0) {
      setCurrent(null);
      return;
    }
    if (current === null || !filtered.some(h => sameEntry(h, current))) {
      const first = filtered[0];
      if (first) setCurrent(entryRef(first));
    }
  }, [filtered, current, indexLoading]);

  // ---------------------------------------------------------------------------------------
  // Fetch the prediction on screen.
  // ---------------------------------------------------------------------------------------
  const currentKey = current === null ? null : entryKey(current);
  useEffect(() => {
    if (current === null) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    // A new hand means the previous hand's explanation must go: leaving it on screen would
    // attribute one hand's reasoning to another.
    setSelectedOrdinal(null);

    predictedJeffBidsApi
      .getHand(current.subcategoryId, current.tableResultId)
      .then(loaded => {
        if (!cancelled) setDetail(loaded);
      })
      .catch(() => {
        if (!cancelled) {
          setDetail(null);
          setDetailError('Unable to load this hand. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `currentKey` stands in for `current`, which is a fresh object on every set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  // ---------------------------------------------------------------------------------------
  // Persist the bookmark. Debounced because stepping quickly through hands would otherwise
  // fire a write per keystroke-speed click, and only the last one matters.
  // ---------------------------------------------------------------------------------------
  useEffect(() => {
    if (!bookmarkRestored.current || indexLoading || categoryId === null) return;

    // Keep the in-memory map current SYNCHRONOUSLY, before the debounce: a switch away and back
    // inside the 600 ms window still resumes correctly, and the map can never disagree with the
    // write that eventually lands.
    const lastTableResultId = current?.tableResultId ?? null;
    bookmarksRef.current.set(scopeKey(outcomeFilter, categoryId, subcategoryId), lastTableResultId);

    const handle = window.setTimeout(() => {
      predictedJeffBidsApi
        .putReviewState({
          lastTableResultId,
          categoryId,
          subcategoryId,
          outcomeFilter,
        })
        // Losing a bookmark write must never interrupt the reviewing.
        .catch(() => undefined);
    }, BOOKMARK_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, categoryId, subcategoryId, outcomeFilter, indexLoading]);

  // ---------------------------------------------------------------------------------------
  // Keep `?id=` and `?sub=` in step with the prediction on screen so the address bar is always a
  // shareable link to it. Replace rather than push, so stepping through hands does not stack a
  // history entry per hand. Reads the params through a ref, so it fires only on a change of
  // prediction, never on its own write.
  // ---------------------------------------------------------------------------------------
  useEffect(() => {
    if (indexLoading || current === null) return;

    const params = searchParamsRef.current;
    if (
      params.get(HAND_ID_PARAM) === String(current.tableResultId) &&
      params.get(SUBCATEGORY_PARAM) === String(current.subcategoryId)
    ) {
      return;
    }

    const next = new URLSearchParams(params);
    next.set(HAND_ID_PARAM, String(current.tableResultId));
    next.set(SUBCATEGORY_PARAM, String(current.subcategoryId));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey, indexLoading, setSearchParams]);

  // Scroll back to the top when the hand changes — the reviewer's eye belongs on the new deal.
  useEffect(() => {
    window.scrollTo?.(0, 0);
  }, [currentKey]);

  const goToPosition = useCallback(
    (position: number) => {
      const target = filtered[position - 1];
      if (target) setCurrent(entryRef(target));
    },
    [filtered]
  );

  // Switching the Category dropdown. The sub-category resets to "All sub-categories" of the new
  // category (the old one belongs to another heading), and the page resumes the target view
  // (new category, all, current outcome) where the reviewer left it — see `landingFor` for the
  // priority order. The setState calls are batched into one render, so the clamp effect (above)
  // is a no-op — no flicker, no jump.
  const handleCategoryChange = useCallback(
    (value: number) => {
      const targetSet = filterHands(index, scopeIds(categories, value, null), outcomeFilter);
      const bookmark = bookmarksRef.current.get(scopeKey(outcomeFilter, value, null));

      setCategoryId(value);
      setSubcategoryId(null);
      setCurrent(landingFor(targetSet, bookmark, current));
    },
    [index, categories, outcomeFilter, current]
  );

  // Switching the Sub-category dropdown within the category.
  const handleSubcategoryChange = useCallback(
    (value: number | null) => {
      const targetSet = filterHands(index, scopeIds(categories, categoryId, value), outcomeFilter);
      const bookmark = bookmarksRef.current.get(scopeKey(outcomeFilter, categoryId, value));

      setSubcategoryId(value);
      setCurrent(landingFor(targetSet, bookmark, current));
    },
    [index, categories, categoryId, outcomeFilter, current]
  );

  // Switching the "AI prediction" (outcome) dropdown. The mirror of the scope switches: it
  // resumes the target view (current scope, target outcome) where the reviewer left it, so
  // moving between All / Matched / Mismatched picks up each outcome's own last-viewed hand.
  const handleOutcomeChange = useCallback(
    (value: PredictionOutcomeFilter) => {
      const targetSet = filterHands(index, scope, value);
      const bookmark = bookmarksRef.current.get(scopeKey(value, categoryId, subcategoryId));

      setOutcomeFilter(value);
      setCurrent(landingFor(targetSet, bookmark, current));
    },
    [index, scope, categoryId, subcategoryId, current]
  );

  const handleCorrectionDirtyChange = useCallback(
    (dirty: boolean) => setCorrectionDirty(dirty),
    []
  );

  // Any move off the current hand — Previous/Next, jump, a filter change, Exit — runs through
  // here. With an unsaved correction draft the move is held back and the field is flagged, so the
  // reviewer is reminded rather than losing what they typed.
  const guardNavigation = useCallback(
    (proceed: () => void) => {
      if (correctionDirty) {
        setRemindUnsavedCorrection(true);
        return;
      }
      proceed();
    },
    [correctionDirty]
  );

  // Once the draft is saved or cleared (no longer dirty), drop the reminder.
  useEffect(() => {
    if (!correctionDirty) setRemindUnsavedCorrection(false);
  }, [correctionDirty]);

  // A new hand starts clean: its editor re-seeds from the stored value, so any stale flag goes.
  useEffect(() => {
    setCorrectionDirty(false);
    setRemindUnsavedCorrection(false);
  }, [currentKey]);

  // Save the shared correction for the prediction on screen, then patch the loaded detail in
  // place so the editor keeps its position (no refetch). The rejection is left to propagate — the
  // editor shows the error and keeps the draft. The identity guard drops a save that resolves
  // after the reviewer has already navigated to another prediction.
  const detailSubcategoryId = detail?.subcategoryId;
  const detailTableResultId = detail?.tableResultId;
  const handleSaveCorrection = useCallback(
    async (correction: string | null) => {
      if (detailSubcategoryId == null || detailTableResultId == null) return;
      await predictedJeffBidsApi.putCorrection(detailSubcategoryId, detailTableResultId, correction);
      setDetail(prev =>
        prev && prev.subcategoryId === detailSubcategoryId && prev.tableResultId === detailTableResultId
          ? { ...prev, correctionExplanation: correction }
          : prev
      );
    },
    [detailSubcategoryId, detailTableResultId]
  );

  const atFirst = currentIndex <= 0;
  const atLast = currentIndex < 0 || currentIndex >= filtered.length - 1;

  const selectedBid =
    selectedOrdinal === null
      ? null
      : detail?.predictedJeffBids.find(b => b.ordinal === selectedOrdinal) ?? null;

  if (indexLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (indexError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="mb-4 text-2xl font-bold text-ink">Predicted Jeff&apos;s Bids by AI</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700" role="alert">
          {indexError}
        </div>
      </div>
    );
  }

  // The filters live in the frame's `controls` slot, so they must be rendered by BOTH branches
  // below: without a hand on screen (an empty filtered set, or a failed detail fetch) there is no
  // frame, and a reviewer who has filtered themselves into a corner still needs the dropdowns and
  // "Clear filters" to get back out.
  // Previous/Next are drawn TWICE (user, 2026-10-09), one definition rendered in two places so
  // the pairs can never disagree on their disabled states or the unsaved-correction guard:
  //   - at the right end of the filter row, above the AI-prediction column, for skimming: the
  //     reviewer reads the prediction and skips on without scrolling, which on a laptop window
  //     they otherwise had to, the status line under the panels being below the fold;
  //   - on the status line's first row, level with "ID: … · Problem n of m" (client decision
  //     2026-07-27), for correcting: the reviewer writes the correction, saves, and the next deal
  //     is one click away without scrolling back up.
  // The bottom pair carries its own test ids so a locator never matches both.
  const navButtons = (suffix: '' | '-bottom') => (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => guardNavigation(() => goToPosition(currentIndex))}
        disabled={atFirst || detailLoading}
        data-testid={`prediction-prev${suffix}`}
        className="rounded-md bg-cream px-4 py-2 text-[14px] font-semibold text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:cursor-not-allowed disabled:opacity-50"
      >
        Previous
      </button>
      <button
        type="button"
        onClick={() => guardNavigation(() => goToPosition(currentIndex + 2))}
        disabled={atLast || detailLoading}
        data-testid={`prediction-next${suffix}`}
        className="rounded-md bg-cream px-4 py-2 text-[14px] font-semibold text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:cursor-not-allowed disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );

  const filterBar = (
    <PredictionFilterBar
      categories={categories}
      categoryId={categoryId}
      onCategoryChange={value => guardNavigation(() => handleCategoryChange(value))}
      subcategoryId={subcategoryId}
      onSubcategoryChange={value => guardNavigation(() => handleSubcategoryChange(value))}
      outcomeFilter={outcomeFilter}
      onOutcomeChange={value => guardNavigation(() => handleOutcomeChange(value))}
      filteredCount={filtered.length}
      currentPosition={currentIndex + 1}
      onJumpToPosition={position => guardNavigation(() => goToPosition(position))}
      disabled={detailLoading}
      trailing={navButtons('')}
    />
  );

  return (
    <div data-testid="predicted-jeff-bids-page">
      {!detail && (
        // No frame to host the controls, so stand up the bare felt shell with the same geometry —
        // the filters keep their place and the message takes the panels' spot.
        <div className="bg-felt-bg pb-6">
          <div className="mx-auto max-w-[1080px] overflow-hidden rounded-[14px] bg-felt shadow-[0_12px_40px_rgba(0,0,0,.4)]">
            <div className="px-[22px] py-[18px]">{filterBar}</div>
            {filtered.length === 0 && (
              <div
                className="mx-[22px] mb-[22px] rounded-[10px] bg-cream p-6 text-ink-soft shadow-[0_2px_6px_rgba(0,0,0,.25)]"
                data-testid="predictions-empty"
              >
                No hands match these filters. Widen the category, sub-category or prediction filter to see more.
              </div>
            )}
            {detailError && (
              <div
                className="mx-[22px] mb-[22px] rounded-[10px] border border-red-200 bg-red-50 p-4 text-red-700"
                role="alert"
              >
                {detailError}
              </div>
            )}
          </div>
        </div>
      )}

      {detail && (
        <TableFrame
          // No top felt padding: the frame's masthead pads the heading itself, and any padding here
          // reads as extra space above it because felt-bg and felt-deep are all but the same green
          // (client decision 2026-07-27). The practice pages keep the default py-6.
          className="bg-felt-bg pb-6"
          controls={filterBar}
          subtitle={
            <span data-testid="prediction-subtitle">
              Predicted Jeff&apos;s Bids by AI
              <span className="ml-2 text-[15px] font-normal text-sand">
                · <CategoryName name={detail.categoryName} /> · <CategoryName name={detail.subcategoryName} />
              </span>
            </span>
          }
          mastheadLinks={
            <button
              type="button"
              onClick={() => guardNavigation(() => navigate('/data-editing'))}
              className="rounded-full bg-brass px-4 py-2 font-semibold text-on-brass hover:bg-brass-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-cream"
            >
              Exit
            </button>
          }
          // The "Jeff holds — <seat>" label rides directly above the hand inside SeatBoardLayout,
          // so it stays over the cards wherever the seat places them — not pinned to the panel
          // top. Suppress the panel-top heading here to avoid showing it twice.
          handLabel={null}
          callLabel="AI prediction"
          hand={
            // Same seat-relative geometry as the practice pages: the hand sits on the side of the
            // board that matches the seat, so the two read as one picture of the table. The
            // "Jeff holds — <seat>" label rides directly above the hand inside SeatBoardLayout, so
            // it stays over the cards wherever the seat places them (the panel-top heading is
            // suppressed with handLabel={null} above).
            <SeatBoardLayout
              seat={detail.jeffSeat}
              handLabel={
                <>
                  Jeff holds — <SeatLabel seat={detail.jeffSeat} />
                </>
              }
              hand={
                detail.jeffHand ? (
                  <HandDisplay hand={detail.jeffHand} />
                ) : (
                  <p className="text-[14px] text-ink-soft">Hand unavailable</p>
                )
              }
              board={
                <VulnerabilityBoard
                  vulnerability={detail.vulnerability ?? ''}
                  decisionSeat={detail.jeffSeat}
                  dealer={isPosition(detail.dealer) ? detail.dealer : undefined}
                />
              }
            />
          }
          secondaryHand={
            // Partner's hand in its OWN panel, between Jeff's and the auction: the AI reasoned about
            // the partnership's auction, so the reviewer needs both hands side by side to judge
            // whether that reasoning holds.
            <div data-testid="partner-hand">
              <h2 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-soft">
                Partner holds — {DIRECTION_NAMES[detail.partnerSeat] ?? detail.partnerSeat}
                {detail.partnerPlayer ? ` (${detail.partnerPlayer})` : ''}
              </h2>
              {detail.partnerHand ? (
                <HandDisplay hand={detail.partnerHand} />
              ) : (
                <p className="text-[14px] text-ink-soft">Hand unavailable</p>
              )}
            </div>
          }
          auction={
            // `!min-h-0` cancels the practice pages' reserved height for ~4 bid rows (index.css,
            // `.auction-scroll`): here the row under the hands holds the AI's reasoning and the
            // editor's correction, so the panels size to the deal and that space goes to them
            // (user, 2026-10-09). A longer auction still grows the panel as before.
            <div className="auction-scroll scrollable !min-h-0">
              {detail.actualAuction.length > 0 ? (
                <AuctionTable
                  auction={detail.actualAuction}
                  vulnerability={detail.vulnerability ?? ''}
                  decisionSeat={detail.jeffSeat}
                  // The table sits on a cream panel, so the default white sticky header would
                  // read as a stripe pasted over the felt theme.
                  headerBgClass="bg-cream"
                />
              ) : (
                <p className="text-[14px] text-ink-soft" data-testid="actual-auction-empty">
                  The tournament auction is unavailable for this hand.
                </p>
              )}
            </div>
          }
          call={
            <PredictedAuctionTable
              auction={detail.predictedAuction}
              jeffBids={detail.predictedJeffBids}
              jeffSeat={detail.jeffSeat}
              selectedOrdinal={selectedOrdinal}
              onSelectBid={setSelectedOrdinal}
              isExactMatch={detail.isExactMatch}
              divergenceIsDeclined={detail.divergenceIsDeclined}
              firstDiffJeffCallNo={detail.divergence?.firstDiffJeffCallNo ?? null}
            />
          }
          explain={
            <BidExplanationPanel
              selectedBid={selectedBid}
              divergence={detail.divergence}
              tableResultId={detail.tableResultId}
              correctionExplanation={detail.correctionExplanation}
              onSaveCorrection={handleSaveCorrection}
              onCorrectionDirtyChange={handleCorrectionDirtyChange}
              remindUnsavedCorrection={remindUnsavedCorrection}
            />
          }
          statusLine={
            <StatusLine
              position={currentIndex + 1}
              total={filtered.length}
              // No streak or score: nothing is answered here, so both would be inventions.
              idLabel="ID"
              idValue={detail.tableResultId}
              scoringType={detail.scoringType}
              tournamentTitle={detail.tournamentTitle}
              showReviewPin={false}
            />
          }
          // The second pair, on the status line's first row rather than the foot of the frame:
          // bottom-aligned, the wrapped "From: <long tournament title>" row pushed them low
          // enough to sit below the fold on a laptop (client decision 2026-07-27).
          footerActionAlign="status-line"
          footerAction={navButtons('-bottom')}
        />
      )}
    </div>
  );
}

/** The sub-category ids of one category, as a set. Empty for an unknown category. */
function subcategoriesOf(categories: PredictionCategory[], categoryId: number | null): Set<number> {
  const category = categories.find(c => c.id === categoryId);
  return new Set(category ? category.subcategories.map(s => s.id) : []);
}

/** The category a sub-category belongs to, or null when the tree does not hold it. */
function categoryOf(categories: PredictionCategory[], subcategoryId: number): number | null {
  const category = categories.find(c => c.subcategories.some(s => s.id === subcategoryId));
  return category ? category.id : null;
}

/**
 * The sub-category ids a scope covers: the one chosen, or every sub-category of the category
 * when "All sub-categories" is selected. There is no all-categories scope.
 */
function scopeIds(
  categories: PredictionCategory[],
  categoryId: number | null,
  subcategoryId: number | null
): Set<number> {
  if (subcategoryId !== null) return new Set([subcategoryId]);
  return subcategoriesOf(categories, categoryId);
}

/**
 * Narrows the index by scope (a set of sub-category ids) and prediction outcome.
 *
 * The three outcome views are the three the hand badge states. `mismatched` deliberately excludes
 * the hands the engine declined to predict (2026-08-20): they are the majority of everything that
 * is not an exact match, and while they sat in this view a reviewer looking for a real
 * disagreement had to read past them.
 */
function filterHands(
  hands: PredictedHandIndexItem[],
  scope: Set<number>,
  outcome: PredictionOutcomeFilter
): PredictedHandIndexItem[] {
  return hands.filter(hand => {
    if (!scope.has(hand.subcategoryId)) return false;
    if (outcome === 'matched') return hand.isExactMatch;
    if (outcome === 'mismatched') return !hand.isExactMatch && !hand.divergenceIsDeclined;
    if (outcome === 'declined') return !hand.isExactMatch && hand.divergenceIsDeclined;
    return true;
  });
}

/**
 * The map key for one filter combination's bookmark. The outcome, the category and the
 * sub-category (null = all of the category) together identify the view, so switching any
 * dropdown resumes that view's own hand.
 */
function scopeKey(
  outcome: PredictionOutcomeFilter,
  categoryId: number | null,
  subcategoryId: number | null
): string {
  return `${outcome}:${categoryId ?? ''}:${subcategoryId ?? ''}`;
}

function entryRef(hand: PredictedHandIndexItem): EntryRef {
  return { subcategoryId: hand.subcategoryId, tableResultId: hand.tableResultId };
}

function entryKey(entry: EntryRef): string {
  return `${entry.subcategoryId}:${entry.tableResultId}`;
}

function sameEntry(a: EntryRef, b: EntryRef): boolean {
  return a.subcategoryId === b.subcategoryId && a.tableResultId === b.tableResultId;
}

/**
 * Where to land when a filter changes, in priority order: (1) the target view's remembered hand,
 * if it is in the new set; (2) the prediction already on screen, if it survives the change; (3)
 * the first prediction of the new set. `bookmark` is the remembered DEAL for the target view (the
 * server stores the deal; within one scope a deal is in one sub-category), or null/undefined.
 */
function landingFor(
  targetSet: PredictedHandIndexItem[],
  bookmark: number | null | undefined,
  current: EntryRef | null
): EntryRef | null {
  if (bookmark != null) {
    const remembered = targetSet.find(h => h.tableResultId === bookmark);
    if (remembered) return entryRef(remembered);
  }
  if (current != null && targetSet.some(h => sameEntry(h, current))) return current;
  const first = targetSet[0];
  return first ? entryRef(first) : null;
}

function isPosition(value: string | null | undefined): value is Position {
  return value === 'N' || value === 'E' || value === 'S' || value === 'W';
}
